import { useEffect, useMemo } from 'react';
import {
  useSelectedOrganizationId,
  useAuthtoken,
  logout,
} from 'stores/useAuthStore';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import request from 'utils/request';
import { setAppLoading, showSnackbar } from 'stores/useUiStore';
import { TOKEN_INVALID } from 'containers/Base/useBaseViewData';
import type { ApiRespBody } from 'types/api';

import type { DeviceGroup, DeviceGroupsState } from '../deviceGroups.types';


export const DEVICE_GROUPS_QUERY_KEY = ['deviceGroups'] as const;

export async function fetchDeviceGroups(
  authToken: string,
  signal?: AbortSignal
): Promise<DeviceGroup[]> {
  const result = await request<ApiRespBody<DeviceGroup[]>>(
    '/api/deviceGroups',
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', authorization: authToken },
      ...(signal ? { signal } : {}),
    }
  );

  if (result?.success) return result.data ?? [];
  if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
  throw new Error(result?.message || 'Failed to load device groups');
}

export function deviceGroupsQueryOptions(
  authToken: string,
  organizationId: number | string | null
) {
  return queryOptions({
    queryKey: [...DEVICE_GROUPS_QUERY_KEY, organizationId],
    queryFn: ({ signal }) => fetchDeviceGroups(authToken, signal),
    staleTime: Infinity,
    enabled: !!authToken,
  });
}

export type UseDeviceGroupsResult = DeviceGroupsState['deviceGroups'] & {
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

export function useDeviceGroups(): UseDeviceGroupsResult {
  const authToken = useAuthtoken();
  const organizationId = useSelectedOrganizationId();

  const query = useQuery(deviceGroupsQueryOptions(authToken, organizationId));

  useEffect(() => {
    if (!(query.error instanceof Error)) return;
    if (query.error.message === TOKEN_INVALID) {
      logout();
    } else {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onDeviceGroupsGet',
          open: true,
          message: query.error.message,
          severity: 'error',
        });
    }
  }, [query.error]);

  const values = useMemo(() => query.data ?? [], [query.data]);

  const lastUpdated = useMemo(
    () =>
      query.dataUpdatedAt
        ? new Date(query.dataUpdatedAt).toISOString()
        : undefined,
    [query.dataUpdatedAt]
  );

  return useMemo(
    () => ({
      values,
      lastUpdated,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      refetch: query.refetch,
    }),
    [values, lastUpdated, query.isLoading, query.isFetching, query.refetch]
  );
}

function useDeviceGroupWrite(opts: {
  caller: string;
  label: string;
  successMessage: string;
  sendRequest: (
    deviceGroup: DeviceGroup,
    authToken: string
  ) => Promise<ApiRespBody<unknown> | null>;
}) {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deviceGroup: DeviceGroup) =>
      opts.sendRequest(deviceGroup, authToken),
    onMutate: () => {
      setAppLoading({ isLoading: true, label: opts.label });
    },
    onSuccess: (result) => {
      if (result?.success) {
        showSnackbar({
            autoHideDuration: 5000,
            caller: opts.caller,
            open: true,
            message: opts.successMessage,
            severity: 'success',
          });
        queryClient.invalidateQueries({ queryKey: DEVICE_GROUPS_QUERY_KEY });
      } else if (result?.message === TOKEN_INVALID) {
        logout();
      } else {
        showSnackbar({
            autoHideDuration: null,
            caller: opts.caller,
            open: true,
            message: result?.message ?? 'Request failed',
            severity: 'error',
          });
      }
    },
    onError: (err: unknown) => {
      showSnackbar({
          autoHideDuration: null,
          caller: opts.caller,
          open: true,
          message: err instanceof Error ? err.message : 'Request failed',
          severity: 'error',
        });
    },
    onSettled: () => {
      setAppLoading({ isLoading: false, label: '' });
    },
  });
}

export function useAddDeviceGroup() {
  return useDeviceGroupWrite({
    caller: 'onDeviceGroupAdd',
    label: 'Adding Device Group',
    successMessage: 'Device Group Added',
    sendRequest: (deviceGroup, authToken) =>
      request<ApiRespBody<unknown>>('/api/deviceGroups', {
        method: 'POST',
        body: JSON.stringify(deviceGroup),
        headers: {
          'Content-Type': 'application/json',
          authorization: authToken,
        },
      }),
  });
}

export function useUpdateDeviceGroup() {
  return useDeviceGroupWrite({
    caller: 'onDeviceGroupUpdate',
    label: 'Updating Device Group',
    successMessage: 'Device Group Updated',
    sendRequest: (deviceGroup, authToken) =>
      request<ApiRespBody<unknown>>(
        `/api/deviceGroups/${deviceGroup.deviceGroupId}`,
        {
          method: 'PUT',
          body: JSON.stringify(deviceGroup),
          headers: {
            'Content-Type': 'application/json',
            authorization: authToken,
          },
        }
      ),
  });
}

export function useDeleteDeviceGroup() {
  return useDeviceGroupWrite({
    caller: 'onDeviceGroupDelete',
    label: 'Deleting Device Group',
    successMessage: 'Device Group Deleted',
    sendRequest: (deviceGroup, authToken) =>
      request<ApiRespBody<unknown>>(
        `/api/deviceGroups/${deviceGroup.deviceGroupId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            authorization: authToken,
          },
        }
      ),
  });
}
